<script setup lang="ts">
    import type { BaseSelectOption } from '~/components/shared/BaseSelect.vue';
    import type {
        AcademyTrainingsListingSort,
        AcademyTrainingsListingSortKey,
        AcademyTrainingsListingSortDirection
    } from '~/types/academy/trainings-listing-sort';
    import { DEFAULT_CATALOG_SORT } from '~/composables/useAcademyTrainingsListing';
    import log from '~/utils/log';

    type SortOption = AcademyTrainingsListingSort & {
        label: string;
    };

    const emit = defineEmits<{
        'update:sort': [AcademyTrainingsListingSort];
    }>();

    const { t } = useI18n();

    const sortOptions = computed<SortOption[]>(() => [
        { label: t('academy.trainings.sort.newestFirst'), ...DEFAULT_CATALOG_SORT },
        { label: t('academy.trainings.sort.oldestFirst'), key: 'createdAt', direction: 'asc' },
        { label: t('academy.trainings.sort.eventDateAsc'), key: 'startsAt', direction: 'asc' },
        { label: t('academy.trainings.sort.nameAsc'), key: 'title', direction: 'asc' },
        { label: t('academy.trainings.sort.nameDesc'), key: 'title', direction: 'desc' },
        { label: t('academy.trainings.sort.mostPoints'), key: 'points', direction: 'desc' }
    ]);

    const DEFAULT_SORT_VALUE = computed(() => toSortValue(sortOptions.value[0]!));

    function toSortValue(option: AcademyTrainingsListingSort): string {
        return `${option.key}:${option.direction}`;
    }

    function fromSortValue(value: string): AcademyTrainingsListingSort {
        if (!value || typeof value !== 'string' || !value.includes(':')) {
            log.warn('Invalid sort value', value);
            return DEFAULT_CATALOG_SORT;
        }

        const [key, direction] = value.split(':') as [
            AcademyTrainingsListingSortKey,
            AcademyTrainingsListingSortDirection
        ];

        return { key, direction };
    }

    const selectOptions = computed<BaseSelectOption[]>(() =>
        sortOptions.value.map((option, index) => ({
            label: option.label,
            value: toSortValue(option),
            selected: index === 0
        }))
    );

    const selectedValue = ref(DEFAULT_SORT_VALUE.value);

    watch(
        selectedValue,
        (value) => {
            emit('update:sort', fromSortValue(value));
        },
        { immediate: true }
    );
</script>

<template>
    <label class="sort-select">
        <span class="label">{{ t('academy.trainings.sort.label') }}</span>
        <BaseSelect
            id="academy-trainings-listing-sort-select"
            v-model="selectedValue"
            class="sort-select"
            :aria-label="t('academy.trainings.sort.label')"
            :options="selectOptions"
        />
    </label>
</template>

<style lang="scss" scoped>
    .sort-select {
        position: relative;
        display: flex;
        box-sizing: border-box;

        .label {
            font-size: var(--font-xs);
            position: absolute;
            transform: translateY(-100%);
            z-index: 1;
            line-height: 1;
        }

        :deep(.base-select) {
            width: 100%;

            select {
                width: 100%;
                border: 1px solid var(--filter-border);
                box-sizing: border-box;
                padding-top: 0.625rem;
                padding-bottom: 0.625rem;
                height: auto;
                line-height: normal;
                background-color: var(--white);
            }
        }

        @media (min-width: $br-l) {
            width: auto;
            min-width: 156px;
        }
    }
</style>
